import React, { useEffect, useState } from "react";
import commonStyles from "@styles/CommonPageLayout.module.css";
import { useAuth } from "@context/auth-context";
import { CreditsSize, CreditsType, validatePromoCode } from "@services/paymentService";
import { redirectToCreditsCheckout } from "@util/stripe-checkout";
import type { IconType } from "react-icons";
import {
    FaBookOpen,
    FaClock,
    FaImages,
    FaHeart,
    FaQrcode,
    FaMapMarkerAlt,
    FaComments,
    FaFileAlt,
    FaVideo,
    FaDonate,
    FaLock,
    FaRobot,
} from "react-icons/fa";
import { GiFlowerPot } from "react-icons/gi";
import { useSearchParams } from "react-router-dom";
import { getRegion, getRootDomain } from "@util/domain";
import { useCountryCode } from "@hooks/country-code";
import styles from "./PurchaseMemorialPage.module.css";

interface Feature {
    icon: IconType | (() => React.ReactNode);
    title: string;
    text: string;
}

const PurchaseMemorialPage = () => {
    const [searchParams] = useSearchParams();
    const { authState } = useAuth();
    const [coupon, setCoupon] = useState<string | undefined>();
    const { user } = authState;
    const memorialId = searchParams.get("memorialId");
    const serviceId = searchParams.get("serviceId"); // Fore create memorial from service
    const type = searchParams.get("type"); // "pet" for pet memorials
    const isPetMemorial = type === "pet";
    const isBusiness = ["business_admin", "business_user"].includes(user?.userType || "");

    const { countryCode, loading, error } = useCountryCode();

    const FEATURES: Feature[] = [
        {
            icon: FaFileAlt,
            title: "Obituary",
            text: `Share a heartfelt story of your ${isPetMemorial ? "pet" : "loved one"}’s life and accomplishments.`,
        },
        {
            icon: FaClock,
            title: "Life Timeline",
            text: "Highlight key moments with an interactive timeline of significant life events.",
        },
        {
            icon: FaImages,
            title: "Gallery",
            text: "Create a collection of cherished photos and videos to celebrate their life.",
        },
        {
            icon: FaHeart,
            title: "Tributes",
            text: "Allow friends and family to leave messages, photos and video tributes.",
        },
        {
            icon: FaQrcode,
            title: "QR Code",
            text: `Provide easy access to the memorial page${isPetMemorial ? "" : ", sign the guest book and make donations"} with a secure, scannable QR code.`,
        },
        {
            icon: FaMapMarkerAlt,
            title: "Grave Location",
            text: "Easily find the exact resting place with an interactive grave location map.",
        },
        ...!isPetMemorial
            ? [
                {
                    icon: FaBookOpen,
                    title: "Digital Service Book",
                    text: "Access service materials online with a convenient digital service book or memorial card.",
                },
            ] : [],
        ...!isPetMemorial && isBusiness
            ? [
                {
                    icon: FaComments,
                    title: "Digital Guest Book",
                    text: "Seamlessly leave heartfelt messages with a digital guest book.",
                }
            ] : [],
        {
            icon: FaDonate,
            title: "Digital Donations",
            text: isPetMemorial ? "Support your chosen animal charity or fundraiser with a direct link" : "Make donations easy with a direct link to your chosen charity or fundraiser.",
        },
        ...!isPetMemorial
            ? [
                {
                    icon: FaVideo,
                    title: "Livestream Link",
                    text: "Provide a link for loved ones to join the service remotely via live stream.",
                },
            ] : [],
        ...!isPetMemorial && !isBusiness
            ? [
                {
                    icon: GiFlowerPot,
                    title: "Send Flowers",
                    text: "Friends and family can send flowers to honour their memory.",
                }
            ] : [],
        {
            icon: FaLock,
            title: "Privacy Controls",
            text: "Customise memorial privacy, control what others see, and restrict access with QR codes.",
        },
        {
            icon: FaRobot,
            title: "AI Content Moderation",
            text: "Keep the memorial space respectful with automated AI content moderation.",
        },
    ];

    let audPrice = Number(process.env.REACT_APP_MEMORIAL_PRICE_STANDARD);
    let usdPrice: Number | null = null;
    let gbpPrice: Number | null = null;
    if (["business_admin", "business_user"].includes(user?.userType || "")) {
        audPrice = Number(process.env.REACT_APP_MEMORIAL_PRICE_BUSINESS);
    } else if (isPetMemorial) {
        audPrice = Number(process.env.REACT_APP_MEMORIAL_PRICE_PET);
        usdPrice = Number(process.env.REACT_APP_MEMORIAL_PRICE_PET_USD);
        gbpPrice = Number(process.env.REACT_APP_MEMORIAL_PRICE_PET_GBP);
    } else {
        usdPrice = Number(process.env.REACT_APP_MEMORIAL_PRICE_STANDARD_USD);
        gbpPrice = Number(process.env.REACT_APP_MEMORIAL_PRICE_STANDARD_GBP);
    }

    useEffect(() => {
        async function process() {
            if (user?.userType === "business_admin" && user?.subscription === "none") {
                const retrievedCoupon = localStorage.getItem("coupon");
                if (retrievedCoupon) {
                    const result = await validatePromoCode(retrievedCoupon);
                    if (result.isValid) setCoupon(retrievedCoupon);
                    else localStorage.removeItem("coupon");
                }
            }
        }
        void process();
    }, [coupon, user?.subscription, user?.userType]);

    const handlePurchase = async () => {
        try {
            await redirectToCreditsCheckout(
                "memorial" as CreditsType,
                1 as CreditsSize,
                memorialId || undefined,
                serviceId || undefined,
            );
        } catch (err) {
            alert("Error purchasing credits. Please try again.");
        }
    };

    const getDisplayPrice = () => {
        if (loading) {
            return { symbol: "$", value: "…", code: "" };
        }
        const aud = { symbol: "$", value: audPrice, code: "AUD" };
        const usd = usdPrice ? { symbol: "$", value: usdPrice, code: "USD" } : null;
        const gbp = gbpPrice ? { symbol: "£", value: gbpPrice, code: "GBP" } : null;

        // International hub: AUD in Australia, GBP in the UK, USD everywhere else,
        // including when the country can't be detected. Business prices are AUD only. A missing USD or GBP
        // price falls back to AUD.
        if (getRegion() === "global") {
            if (!error && countryCode === "AU") return aud;
            if (!error && countryCode === "GB" && gbp) return gbp;
            return usd || aud;
        }

        // AU hub: USD in the US, GBP in the UK, AUD everywhere else
        if (!error) {
            if (countryCode === "US" && usd) return usd;
            if (countryCode === "GB" && gbp) return gbp;
        }

        return aud;
    };

    const displayPrice = getDisplayPrice();

    return (
        <div className={`${commonStyles.container} ${styles.pageWrapper}`}>
            {/* Hero */}
            <section className={styles.heroSection}>
                <h1 className={styles.heroTitle}>{`${isBusiness ? "Create" : "Upgrade"} Your Forever${isPetMemorial ? " Pet" : ""} Memorial${isBusiness ? "+" : ""}`}</h1>
                <p className={styles.heroSubtitle}>
                    {`Celebrate and preserve their life ${isPetMemorial ? "" : "and legacy "}for generations.`}
                </p>
            </section>

            {/* Features */}
            <section className={styles.featuresSection}>
                <h2 className={styles.sectionTitle}>Everything Included</h2>
                <div
                    className={`${styles.featuresGrid} ${
                        isPetMemorial ? styles.petGrid : ""
                    }`}
                >
                    {FEATURES.map((f, i) => {
                        const Icon = f.icon as any;
                        return (
                            <div key={i} className={styles.featureCard}>
                                <div className={styles.icon}>
                                    {typeof Icon === "function" ? <Icon /> : Icon}
                                </div>
                                <h3>{f.title}</h3>
                                <p>{f.text}</p>
                            </div>
                        );
                    })}
                </div>
                <p className={styles.faqLinkCentered}>
                    <a href={`${getRootDomain()}/faq`} target="_blank" rel="noreferrer">See FAQs →</a>
                </p>
            </section>

            {/* CTA */}
            <section className={styles.ctaSection}>
                <div className={styles.ctaContent}>
                    <h2 className={styles.ctaTitle}>One Time Payment. Lifetime Access.</h2>
                    <p className={styles.ctaSubtitle}>
                        {`${isBusiness ? "Create" : "Upgrade"} your Forever ${isPetMemorial ? "Pet " : ""}Memorial${isBusiness ? "+" : ""} today — all features included for a single payment of`}
                        {" "}
                        <strong>{`${displayPrice.symbol}${displayPrice.value}`}</strong>
                    </p>
                    <button className={styles.ctaButton} onClick={handlePurchase}>
                        Buy Now
                    </button>
                    <p className={styles.ctaNote}>No subscriptions. No hidden fees. Just lasting remembrance.</p>
                    <p className={styles.faqLink}>
                        <a href={`${getRootDomain()}/faq`} target="_blank" rel="noreferrer">Questions? Read the FAQ →</a>
                    </p>
                </div>
            </section>
        </div>
    );
};

export default PurchaseMemorialPage;
